// oxlint-disable max-lines
import { AnalyticsBrowser, type EventProperties, type UserTraits } from '@segment/analytics-next';
import { getSegmentKey } from '@/segment/segment-config';
import { screenSizePlugin } from './segment-screen-size-plugin';

import {
  removeReservationGroupIdPlugin,
  removeReservationGroupIdFromPageObject,
  removeReservationGroupIdFromProperties
} from './segment-remove-reservation-group-id-plugin';

import {
  experimentConfigs,
  getAbTestGroup,
  getExperimentsSegmentProperties
} from '@/utils/ab-testing';

import { mergeSegmentTrackEventProperties } from '@/segment/integration/segment-track-properties';
import { getSegmentConsent, type SegmentConsentCoordinator, type SegmentConsent } from './segment-consent';
import { sharedSegmentIdentity } from './segment-identity';
import { getSegmentUrlCommands } from './segment-url-commands';
import { debugSegmentEvent, debugSegmentInitialization } from './segment-debug-logger';
import { getSegmentEventQueue } from './segment-event-queue';
import type { SegmentClientName, SegmentEventQueueEvent, BeaconEventOptions, SegmentManagerInterface } from '@/typings/segment-manager';

type SegmentEventContext = NonNullable<BeaconEventOptions['context']>;

interface SegmentManagerOptions {
  readonly name: SegmentClientName;
  readonly consent: SegmentConsentCoordinator;
  readonly resetAll: () => void;
}

interface SegmentDelivery {
  readonly generation: number;
  readonly analytics: AnalyticsBrowser;
  readonly context: SegmentEventContext;
  readonly consent: Promise<SegmentConsent>;
}

export class SegmentManager implements SegmentManagerInterface {
  private readonly analyticsInstance = new AnalyticsBrowser();
  private analyticsInitialized = false;
  private abTestingInitialized = false;
  readonly name: SegmentClientName;
  readonly writeKey: string;
  private readonly consent: SegmentConsentCoordinator;
  private readonly resetAll: () => void;
  private readonly eventQueue: SegmentEventQueueEvent[];
  private loading: Promise<void> | null = null;
  private draining: Promise<void> | null = null;
  private deliveryGeneration = 0;
  private apiHost: string | undefined;

  private readonly consentTarget = {
    load: async (): Promise<void> => {
      await this.loadAnalyticsInstance();
      await this.processEventQueue();
    },
    suspend: (): void => {
      this.analyticsInitialized = false;
      this.deliveryGeneration += 1;
    },
    hasStarted: (): boolean => this.loading !== null
  };

  constructor(options: SegmentManagerOptions) {
    this.name = options.name;
    this.writeKey = getSegmentKey(this.name);
    this.consent = options.consent;
    this.resetAll = options.resetAll;

    this.eventQueue = getSegmentEventQueue(this.name);
  }

  private static buildSegmentOptions(
    context: SegmentEventContext, consent: SegmentConsent, options?: BeaconEventOptions
  ): BeaconEventOptions {
    context.consent = consent;

    return {
      ...options,
      context
    };
  }

  private get isBooking(): boolean {
    return this.name === 'booking';
  }

  private experimentProperties(): EventProperties {
    if (this.isBooking === false) {
      return {};
    }

    try {
      return getExperimentsSegmentProperties();
    } catch {
      // Experiment storage must not prevent delivery of the caller's event.
      return {};
    }
  }

  private initializeAbTestingAfterDelivery(): void {
    void this.initializeAbTesting();
  }

  private isDeliveryReady(): boolean {
    return this.analyticsInitialized && this.consent.isAllowed;
  }

  private prepareDelivery(options?: BeaconEventOptions): SegmentDelivery {
    const generation = this.deliveryGeneration;
    const analytics = this.getAnalyticsInstance();
    const context = { ...options?.context };
    const consent = getSegmentConsent();

    // Callers await the consent promise directly to preserve their delivery turn.
    return {
      generation,
      analytics,
      context,
      consent
    };
  }

  private canDeliver(generation: number): boolean {
    return generation === this.deliveryGeneration && this.isDeliveryReady();
  }

  private static synchronizeIdentity(anonymousId?: string): void {
    if (sharedSegmentIdentity.isPrepared) {
      sharedSegmentIdentity.synchronize(anonymousId);
    }
  }

  private tryEnqueue(event: SegmentEventQueueEvent): boolean {
    if (this.isDeliveryReady() && this.draining === null && this.eventQueue.length === 0) {
      return false;
    }

    this.eventQueue.push(event);

    let eventName = '';
    let debugProperties: EventProperties = {};

    switch (event.type) {
      case 'track': {
        ({ eventName } = event);
        debugProperties = event.payload;
        break;
      }

      case 'page': {
        eventName = event.pageName;
        debugProperties = event.payload;
        break;
      }

      case 'identify': {
        eventName = 'Identify';
        debugProperties = {
          hasUserId: event.userId !== null,
          traitKeys: Object.keys(event.traits)
        };
        break;
      }

      case 'group': {
        eventName = 'Group';
        debugProperties = {
          hasGroupId: event.groupId.length > 0,
          traitKeys: Object.keys(event.traits)
        };
        break;
      }
    }

    debugSegmentEvent({
      source: this.name,
      status: 'queued',
      name: eventName
    }, debugProperties, event.options);

    if (this.isDeliveryReady()) {
      void this.processEventQueue();
    }

    return true;
  }

  private async loadAnalyticsInstance(): Promise<void> {
    this.loading ??= this.loadOwnedAnalytics();

    return this.loading;
  }

  private async loadOwnedAnalytics(): Promise<void> {
    sharedSegmentIdentity.prepare();

    const website = this.isBooking === false;

    if (this.isBooking) {
      const commands = getSegmentUrlCommands();

      if (commands.anonymousId !== undefined) {
        sharedSegmentIdentity.setAnonymousId(commands.anonymousId);
      }

      this.eventQueue.unshift(...commands.events);
    }

    const group = website ? {
      cookie: { key: 'ajs_website_group_id' },
      localStorage: { key: 'ajs_website_group_properties' }
    } : undefined;

    const anonymousId = sharedSegmentIdentity.getAnonymousId();

    if (anonymousId !== null) {
      // Segment flushes this before destinations initialize.
      void this.analyticsInstance.setAnonymousId(anonymousId);
    }

    await this.analyticsInstance.load({
      writeKey: this.writeKey,
      plugins: [screenSizePlugin, removeReservationGroupIdPlugin]
    }, {
      // Owned URL commands replay through booking, with normal consent context.
      useQueryString: false,
      // The shared coordinator owns persistence; clients keep its current identity.
      user: { persist: false },
      group,
      integrations: {
        'Segment.io': { deliveryStrategy: { config: { keepalive: true } } }
      }
    });

    const analytics = this.analyticsInstance.instance;

    if (analytics !== undefined) {
      sharedSegmentIdentity.register(analytics.user(), () => {
        analytics.reset();
      });
      this.apiHost = analytics.settings.apiHost;
    }

    // Preserve the existing booking endpoint for older SDK adapters without metadata.
    this.apiHost ??= website ? undefined : 'events.eu1.segmentapis.com/v1';
    this.analyticsInitialized = this.consent.isAllowed;
  }

  async initializeAbTesting(): Promise<void> {
    if (this.isBooking === false || this.abTestingInitialized) {
      return;
    }

    debugSegmentInitialization('Initializing A/B testing');
    this.abTestingInitialized = true;

    try {
      const abGroupTraits: Record<string, string> = {};

      for (const config of experimentConfigs) {
        // Active experiments assign; inactive ones join only via a non-prod forced group.
        const testGroup = getAbTestGroup(config.key);

        if (testGroup !== null) {
          debugSegmentInitialization(`Setting A/B test group: ${config.key} - ${testGroup}`);
          abGroupTraits[config.key] = testGroup;
        }
      }

      if (Object.keys(abGroupTraits).length > 0) {
        await this.identify(null, abGroupTraits);

        debugSegmentInitialization('A/B testing initialized');
      } else {
        debugSegmentInitialization('No A/B tests to initialize');
      }
    } catch (error) {
      debugSegmentInitialization(`Failed to initialize A/B testing: ${String(error)}`);
    }
  }

  reset(): void {
    this.resetAll();
  }

  resetUnregisteredClient(): void {
    if (this.analyticsInitialized && this.analyticsInstance.instance === undefined) {
      void this.analyticsInstance.reset();
    }
  }

  clearEventQueue(): void {
    this.eventQueue.length = 0;
    this.deliveryGeneration += 1;
    this.abTestingInitialized = false;
  }

  async track(eventName: string, properties?: EventProperties, options?: BeaconEventOptions): Promise<void> {
    if (this.tryEnqueue({
      type: 'track',
      eventName,
      payload: properties ?? {},
      options
    })) {
      return;
    }

    await this.deliverTrack(eventName, properties, options);
  }

  private async deliverTrack(
    eventName: string,
    properties?: EventProperties,
    options?: BeaconEventOptions
  ): Promise<void> {
    // Use beacon for critical events
    if (options?.critical === true && this.apiHost !== undefined && SegmentManager.supportsBeacon()) {
      const experimentProperties = this.experimentProperties();

      await this.sendViaBeacon(eventName,
        {
          properties: mergeSegmentTrackEventProperties(eventName, properties, experimentProperties),
          options
        });

      return;
    }

    const { generation, analytics, context, consent } = this.prepareDelivery(options);
    const currentOptions = SegmentManager.buildSegmentOptions(context, await consent, options);

    try {
      void this.initializeAbTesting();

      const experimentSegmentProperties = this.experimentProperties();

      const eventProperties = mergeSegmentTrackEventProperties(
        eventName,
        properties,
        experimentSegmentProperties
      );

      if (this.canDeliver(generation) === false) {
        return;
      }

      SegmentManager.synchronizeIdentity(currentOptions.anonymousId);

      debugSegmentEvent({
        source: this.name,
        status: 'dispatching',
        name: eventName
      }, eventProperties, currentOptions);
      await analytics.track(eventName, eventProperties, currentOptions);
    } catch {
      // Silently fail to avoid console warnings
    }
  }

  async page(pageName: string, properties?: EventProperties, options?: BeaconEventOptions): Promise<void> {
    if (this.tryEnqueue({
      type: 'page',
      pageName,
      payload: properties ?? {},
      options
    })) {
      return;
    }

    await this.deliverPage(pageName, properties, options);
  }

  private async deliverPage(
    pageName: string,
    properties?: EventProperties,
    options?: BeaconEventOptions
  ): Promise<void> {
    const { generation, analytics, context, consent } = this.prepareDelivery(options);
    const currentOptions = SegmentManager.buildSegmentOptions(context, await consent, options);

    // Preserve the page delivery turn from the original async options builder.
    await Promise.resolve();

    const currentProperties = {
      ...properties,
      ...this.experimentProperties()
    };

    try {
      if (this.canDeliver(generation) === false) {
        return;
      }

      SegmentManager.synchronizeIdentity(currentOptions.anonymousId);

      debugSegmentEvent({
        source: this.name,
        status: 'dispatching',
        name: pageName
      }, currentProperties, currentOptions);
      await analytics.page(pageName, currentProperties, currentOptions);
      this.initializeAbTestingAfterDelivery();
    } catch {
      // Silently fail to avoid console warnings
    }
  }

  async identify(userId: string | null, traits?: UserTraits, options?: BeaconEventOptions): Promise<void> {
    if (this.tryEnqueue({
      type: 'identify',
      userId,
      traits: traits ?? {},
      options
    })) {
      return;
    }

    await this.deliverIdentify(userId, traits, options);
  }

  private async deliverIdentify(
    userId: string | null,
    traits?: UserTraits,
    options?: BeaconEventOptions
  ): Promise<void> {
    const { generation, analytics, context, consent } = this.prepareDelivery(options);
    const currentOptions = SegmentManager.buildSegmentOptions(context, await consent, options);

    try {
      if (this.canDeliver(generation) === false) {
        return;
      }

      if (sharedSegmentIdentity.isPrepared) {
        sharedSegmentIdentity.identify(userId, traits, currentOptions.anonymousId);
      }

      debugSegmentEvent({
        source: this.name,
        status: 'dispatching',
        name: 'Identify'
      }, {
        hasUserId: userId !== null,
        traitKeys: Object.keys(traits ?? {})
      }, currentOptions);
      await analytics.identify(userId, traits, currentOptions);

      SegmentManager.synchronizeIdentity();
    } catch {
      // Silently fail to avoid console warnings
    }
  }

  async group(groupId: string, traits?: UserTraits, options?: BeaconEventOptions): Promise<void> {
    if (this.tryEnqueue({
      type: 'group',
      groupId,
      traits: traits ?? {},
      options
    })) {
      return;
    }

    await this.deliverGroup(groupId, traits, options);
  }

  private async deliverGroup(
    groupId: string,
    traits?: UserTraits,
    options?: BeaconEventOptions
  ): Promise<void> {
    const { generation, analytics, context, consent } = this.prepareDelivery(options);
    const currentOptions = SegmentManager.buildSegmentOptions(context, await consent, options);

    try {
      if (this.canDeliver(generation) === false) {
        return;
      }

      SegmentManager.synchronizeIdentity(currentOptions.anonymousId);

      debugSegmentEvent({
        source: this.name,
        status: 'dispatching',
        name: 'Group'
      }, {
        hasGroupId: groupId.length > 0,
        traitKeys: Object.keys(traits ?? {})
      }, currentOptions);
      await analytics.group(groupId, traits, currentOptions);
    } catch {
      // Silently fail to avoid console warnings
    }
  }

  private async processQueuedEvent(event: SegmentEventQueueEvent): Promise<void> {
    switch (event.type) {
      case 'page': {
        await this.deliverPage(event.pageName, event.payload, event.options);

        break;
      }

      case 'track': {
        await this.deliverTrack(event.eventName, event.payload, event.options);

        break;
      }

      case 'identify': {
        await this.deliverIdentify(event.userId, event.traits, event.options);

        break;
      }

      case 'group': {
        await this.deliverGroup(event.groupId, event.traits, event.options);

        break;
      }
    }
  }

  async processEventQueue(): Promise<void> {
    this.draining ??= this.drainEventQueue();

    return this.draining;
  }

  private async drainEventQueue(): Promise<void> {
    // Yield so concurrent calls see the assigned single drain operation.
    await Promise.resolve();

    try {
      await this.processNextQueuedEvent();
    } finally {
      this.draining = null;

      // A call can append after the final empty check while this drain is still pending.
      if (this.isDeliveryReady() && this.eventQueue.length > 0) {
        await this.processEventQueue();
      }
    }
  }

  private async processNextQueuedEvent(): Promise<void> {
    if (this.isDeliveryReady() === false) {
      return;
    }

    const [event] = this.eventQueue;

    if (event === undefined) {
      return;
    }

    this.eventQueue.shift();

    try {
      await this.processQueuedEvent(event);
    } catch {
      debugSegmentInitialization(`Failed to process queued ${event.type} event`);
    }

    await this.processNextQueuedEvent();
  }

  async initializeWithConsentListening(): Promise<boolean> {
    return this.consent.initialize(this.consentTarget);
  }

  async getAnonymousId(): Promise<string | null> {
    if (this.consent.isAllowed === false) {
      return null;
    }

    const generation = this.deliveryGeneration;

    try {
      const consent = await getSegmentConsent();

      if (consent.categoryPreferences.functional === false || generation !== this.deliveryGeneration) {
        return null;
      }

      return sharedSegmentIdentity.getAnonymousId();
    } catch {
      // Unavailable analytics identity must not block callers.
      return null;
    }
  }

  getAnalyticsInstance(): AnalyticsBrowser {
    return this.analyticsInstance;
  }

  private static supportsBeacon(): boolean {
    if (typeof navigator === 'undefined' || !('sendBeacon' in navigator)) {
      return false;
    }

    return true;
  }

  private async sendViaBeacon(
    eventName: string,
    eventData: {
      readonly properties?: EventProperties;
      readonly options?: BeaconEventOptions;
    }
  ): Promise<void> {
    try {
      const { generation, analytics, context, consent } = this.prepareDelivery(eventData.options);
      const currentOptions = SegmentManager.buildSegmentOptions(context, await consent, eventData.options);
      const segmentKey = this.writeKey;
      // Build payload matching the working Segment format exactly
      const timestamp = new Date().toISOString();
      const properties = { ...eventData.properties };
      const page = context.page === undefined ? undefined : { ...context.page };

      removeReservationGroupIdFromPageObject(page);
      removeReservationGroupIdFromProperties(properties);

      if (page !== undefined) {
        context.page = page;
      }

      const payload: Record<string, unknown> = {
        timestamp,
        integrations: { 'Segment.io': true },
        type: 'track',
        event: eventName,
        properties,
        context,
        messageId: `ajs-next-${Date.now()}-${Math.random().toString(36)
          .slice(2, 25)}`,
        writeKey: segmentKey,
        sentAt: timestamp,
        // eslint-disable-next-line @typescript-eslint/naming-convention
        _metadata: {
          bundled: ['Segment.io'],
          unbundled: [],
          bundledIds: []
        }
      };

      SegmentManager.synchronizeIdentity();

      // Add user identifiers if available
      try {
        const user = await analytics.user();
        const userId = user.id?.();

        if (userId !== undefined && userId !== null) {
          payload.userId = userId;
        }

        if (user.anonymousId() !== null) {
          payload.anonymousId = user.anonymousId();
        }
      } catch {
        // Fallback if user methods not available
      }

      if (this.canDeliver(generation) === false || this.apiHost === undefined) {
        return;
      }

      const url = `https://${this.apiHost}/t`;
      const requestData = JSON.stringify(payload);
      // Send via beacon using raw string to match working example
      const isSuccessful = navigator.sendBeacon(url, requestData);
      const status = isSuccessful ? 'beacon accepted' : 'beacon rejected';

      debugSegmentEvent(
        {
          source: this.name,
          status,
          name: eventName
        },
        properties,
        currentOptions
      );
    } catch (error) {
      debugSegmentInitialization(`Beacon send error for track: ${String(error)}`);
    }
  }
}
